/* What a detection and a posture mean on screen, decided once.
 *
 * **The judgement this file holds is that the page must never overstate the engine's restraint, in
 * either direction.** Three things follow from it.
 *
 * `notified` is tri-state on the wire and stays three renderings here. `null` is "no notification
 * was attempted", which while observing is every row; `false` is "evaluated and delivered
 * nowhere", which includes a tenant whose routing sends this severity nowhere. Collapsing the two
 * would report a deliberate non-send as a failed one, or a silent escalation as a quiet success.
 *
 * `would-have-escalated` is its own outcome and never folded into either neighbour. It is the row
 * whose evidence cleared the bar and whose mode held it back — precisely what will escalate the day
 * escalation is turned on, and the only rows an observation period is really about.
 *
 * And the counts the tiles show come from the platform's own `/summary`, not from the page of rows
 * on screen. A page is 25 detections; a review spans hundreds. Counting what is rendered would make
 * "alerts not routed" mean "not routed on this page", which is the same number wearing a false
 * scope.
 */

import type {
  AutonomousSummary,
  Detection,
  DetectionVerdict,
  ModeReport,
} from '@/shared/api/generated/ai-autonomous';

export type { DetectionVerdict };

/** How a row reads: did it escalate, was it held back, or was the evidence too thin.
 *
 * Three, matching the contract's three verdicts. The design also draws a fourth, "Blocked by
 * policy" — for a `PolicyHook` that would decline a decision the arithmetic permitted. **No such
 * verdict exists on the wire and no deployment wires that hook**, so rendering one would be a
 * screen describing a control the platform does not have. It is left out rather than faked; the day
 * the hook lands, the verdict arrives with it and this union gains its fourth member.
 */
export type Outcome = 'escalated' | 'would-have-escalated' | 'below-threshold';

export function outcomeOf(detection: Detection): Outcome {
  if (detection.escalated) return 'escalated';
  switch (detection.verdict) {
    case 'escalated':
    case 'withheld':
      /* Withheld *is* "would have escalated": the evidence cleared the bar and the mode held it
         back. It is also the verdict for an escalation that opened nothing, whose reason the
         rationale carries — and both read the same way to a reviewer, which is that nobody was
         told. */
      return 'would-have-escalated';
    case 'below_threshold':
      return 'below-threshold';
  }
}

export type OutcomeTone = 'acted' | 'pending' | 'held';

/** Total over `Outcome` with `satisfies`, so a fourth outcome is a compile error here.
 *
 * A `switch` with a `default` would give an unknown outcome the *held* tone — the quietest one —
 * which is the failure mode §9 records for `verdictTone` in the evals slice: a fifth verdict
 * silently losing its badge.
 */
const OUTCOME_TONES = {
  escalated: 'acted',
  /* Not `held`: a decision that would have escalated is not a healthy state to skim past — it is
     the one a reviewer is here to judge. */
  'would-have-escalated': 'pending',
  'below-threshold': 'held',
} satisfies Record<Outcome, OutcomeTone>;

export function outcomeTone(outcome: Outcome): OutcomeTone {
  return OUTCOME_TONES[outcome];
}

export type AlertReading = 'sent' | 'not-routed' | 'not-attempted';

/** The three states `notified` carries, named.
 *
 * Read from `notified` alone and never inferred from `escalated`: a row can have opened an incident
 * and delivered nothing, which is the state the "alerts not routed" tile exists for.
 */
export function alertReading(detection: Detection): AlertReading {
  if (detection.notified === null) return 'not-attempted';
  return detection.notified ? 'sent' : 'not-routed';
}

export type AlertTone = 'ok' | 'warn' | 'none';

const ALERT_TONES = {
  sent: 'ok',
  /* Amber, not neutral. An incident that reached nobody looks identical to a healthy estate on
     every other surface, so this is the one reading that has to carry a colour. */
  'not-routed': 'warn',
  'not-attempted': 'none',
} satisfies Record<AlertReading, AlertTone>;

export function alertTone(reading: AlertReading): AlertTone {
  return ALERT_TONES[reading];
}

/** Confidence as a percentage, no decimals — the design's `toFixed(0)`.
 *
 * The threshold beside it carries two decimals in the rationale, where the arithmetic is being
 * defended; the cell is being scanned, and 83% against 60% is the comparison a reader makes.
 */
export function formatConfidence(value: number): string {
  return `${Math.round(value * 100).toString()}%`;
}

/** Whether this row's confidence cleared its own threshold.
 *
 * From the row's own stored threshold rather than from a constant, so a detection recorded before
 * the bar moved still reads against the bar it was judged by.
 */
export function clearedThreshold(detection: Detection): boolean {
  return detection.confidence >= detection.threshold;
}

/** Whether this row's run counts are a measurement or an absence.
 *
 * **Found by looking at the live screen, and it is absence-is-not-zero at three cells on one row.**
 * A detection recorded under the previous design carries no run counts at all — that design stored
 * a confidence and a rationale and nothing to reconstruct them from — so migration 0092 could not
 * backfill `runs`, `failing_runs` or `violations` and left them at the column default.
 *
 * Rendered raw, such a row reads `0 of 0 failing`, `0 violations`, and a confidence of 83% beside
 * them. That is not merely thin, it is **internally impossible**: no share of zero runs produces
 * 0.83, so the row states an arithmetic the platform could not have performed. The confidence and
 * the threshold *were* recorded and are shown; the evidence was not, and has to say so.
 *
 * Keyed on `runs` rather than on all three, because `failing_runs: 0` and `violations: 0` are both
 * legitimate for a real assessment — a binding that ran twelve times and failed none is exactly
 * what the below-threshold verdict is for.
 */
export function evidenceRecorded(detection: Detection): boolean {
  return detection.runs > 0;
}

/** Whether this row was judged against a bar at all.
 *
 * Same cause as `evidenceRecorded`, third cell. The previous design recorded a *reversal* as a
 * decision with a threshold of zero — nothing was being judged, a person had asked for something —
 * and `needs 0%` renders that as a bar every confidence clears, which is a bar that is not one.
 *
 * Unreachable for anything this design writes, since every detection carries the module's own
 * `ESCALATION_THRESHOLD`. It is a fact about stored rows rather than about the engine, which is
 * exactly why the screen has to answer it.
 */
export function thresholdRecorded(detection: Detection): boolean {
  return detection.threshold > 0;
}

/** A rule's name, or `null` when the row does not carry one.
 *
 * Same cause as `evidenceRecorded` and the same rule: an empty string in the cell a steward reads
 * first is indistinguishable from a screen that failed to render, so the absence is named rather
 * than printed. Whitespace counts as absent — a name of one space is not a name.
 */
export function ruleLabel(detection: Detection): string | null {
  const name = detection.rule_name.trim();
  return name.length === 0 ? null : name;
}

/** Days elapsed in the observation window, capped at the period.
 *
 * **Capped, and the design says why**: the period can only ever be lengthened, so an uncapped count
 * against a moving denominator could read "day 61 of 60" — and a progress bar past 100% is a
 * screen contradicting the clock it is drawing.
 */
export function dayOf(report: ModeReport, now: Date): number {
  const started = new Date(report.shadow_started_at).getTime();
  const elapsed = Math.floor((now.getTime() - started) / (24 * 60 * 60 * 1000));
  return Math.max(0, Math.min(elapsed, report.observation_days));
}

/** How far through the window, 0–100. */
export function progressPercent(report: ModeReport, now: Date): number {
  if (report.observation_days <= 0) return 100;
  return Math.min(100, Math.round((dayOf(report, now) / report.observation_days) * 100));
}

export type PeriodEdit =
  | { readonly kind: 'valid' }
  | { readonly kind: 'out-of-range' }
  | { readonly kind: 'would-shorten'; readonly current: number }
  | { readonly kind: 'unchanged' };

/** Whether a typed period may be saved, and why not when it may not.
 *
 * **The client never enforces this — it explains it.** The substrate holds the range as a CHECK and
 * the direction as a trigger, and the service refuses with a 422 or a 409. What this does is stop a
 * reader submitting a value they can already be told is refused, which is a courtesy rather than a
 * control.
 *
 * `unchanged` is separate from `valid` because Save must be off for a value that is in range and
 * identical: a button that appears to do something and writes the same number teaches a user that
 * the screen does not respond.
 */
export function periodEdit(raw: string, report: ModeReport): PeriodEdit {
  const days = Number.parseInt(raw, 10);
  if (
    Number.isNaN(days) ||
    days < report.min_observation_days ||
    days > report.max_observation_days
  ) {
    return { kind: 'out-of-range' };
  }
  if (days < report.observation_days) {
    return { kind: 'would-shorten', current: report.observation_days };
  }
  if (days === report.observation_days) return { kind: 'unchanged' };
  return { kind: 'valid' };
}

export function canSavePeriod(edit: PeriodEdit): boolean {
  return edit.kind === 'valid';
}

/** Whether the field should read as an error: a refusable value, not merely an unchanged one. */
export function periodIsRefusable(edit: PeriodEdit): boolean {
  return edit.kind === 'out-of-range' || edit.kind === 'would-shorten';
}

/** Who last configured the period, and when — or `null` when it never has been.
 *
 * The two fields are both-or-neither on the wire (`observation_period_changed_by` is null exactly
 * when `_at` is), which is a fact about the API worth checking rather than assuming: rendering one
 * without confirming the other would print "Last changed by null" the first time a deployment's
 * audit read disagreed with itself.
 */
export interface PeriodChange {
  readonly at: string;
  readonly by: string;
}

export function periodChangeOf(report: ModeReport): PeriodChange | null {
  if (report.observation_period_changed_at === null) return null;
  if (report.observation_period_changed_by === null) return null;
  return {
    at: report.observation_period_changed_at,
    by: report.observation_period_changed_by,
  };
}

/** The counts a review reads, from the platform's own aggregate.
 *
 * `notRouted` is the figure an operator most needs, which is why it is the one that carries a tone.
 */
export interface Tallies {
  readonly persistent: number;
  readonly incidents: number;
  readonly notified: number;
  readonly notRouted: number;
}

export function talliesOf(summary: AutonomousSummary): Tallies {
  return {
    persistent: summary.persistent,
    incidents: summary.incidents,
    notified: summary.notified,
    notRouted: summary.not_routed,
  };
}
